import { useCallback, useEffect, useRef, useState } from "react";
import * as crm from "@/lib/crmApi";

const message = error =>
  error?.response?.data?.detail?.message ||
  (typeof error?.response?.data?.detail === "string" ? error.response.data.detail : null) ||
  // Pydantic returns a list of field errors; show the first rather than "[object Object]".
  error?.response?.data?.detail?.[0]?.msg ||
  error?.message ||
  "Could not reach the server";

const call = async fn => {
  try {
    return { ok: true, data: await fn() };
  } catch (error) {
    return { ok: false, error: message(error) };
  }
};

/**
 * Everything the CRM screens need, against real endpoints.
 *
 * Every action on this page used to be a toast. "Add customer" opened nothing,
 * "Receive" payment opened nothing, loyalty points were announced and discarded,
 * campaigns were "ready for the local messaging gateway" and never recorded, and the
 * khata bulk payment queued an action the dispatcher only audited — so an outstanding
 * balance was never actually reduced.
 */
export function useCrm(token, { crmLists = true, khataLists = true } = {}) {
  const [customers, setCustomers] = useState([]);
  const [tiers, setTiers] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [feedback, setFeedback] = useState([]);
  const [summary, setSummary] = useState(null);
  const [messages, setMessages] = useState([]);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  // Search runs on every keystroke. Without an ordering guard the response for
  // "98765" can land after the one for "9876543210" and repaint the list with
  // everyone whose number starts 98765, while the box reads the full number — the
  // operator then taps Points or Orders on the wrong person's row.
  const searchSeq = useRef(0);

  const load = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    const results = await Promise.allSettled([
      crm.listCustomers(null, token),
      crm.listTiers(token),
      khataLists ? crm.listKhataAccounts(token) : Promise.resolve([]),
      // Campaigns, feedback and the message queue need "CRM: open campaigns,
      // messages and feedback" (2026-10-20). Without it they are not asked for,
      // so the till's quick panel does not show a refusal for someone without CRM.
      crmLists ? crm.listCampaigns(token) : Promise.resolve([]),
      crmLists ? crm.listFeedback({}, token) : Promise.resolve([]),
      crmLists ? crm.feedbackSummary(token) : Promise.resolve(null),
      crmLists ? crm.listMessages("pending", token) : Promise.resolve([]),
    ]);
    const [c, t, a, ca, f, s, m] = results;
    // Partial failure is normal: a cashier can read customers but not the permission
    // catalogue. Blanking the whole page because one panel is forbidden is worse.
    if (c.status === "fulfilled") setCustomers(c.value);
    if (t.status === "fulfilled") setTiers(t.value);
    if (a.status === "fulfilled") setAccounts(a.value);
    if (ca.status === "fulfilled") setCampaigns(ca.value);
    if (f.status === "fulfilled") setFeedback(f.value);
    if (s.status === "fulfilled") setSummary(s.value);
    if (m.status === "fulfilled") setMessages(m.value);
    const failed = results.find(r => r.status === "rejected");
    setError(failed ? message(failed.reason) : null);
    setLoading(false);
  }, [token, crmLists, khataLists]);

  useEffect(() => {
    load();
  }, [load]);

  const withReload = fn => async (...args) => {
    const result = await call(() => fn(...args));
    if (result.ok) await load();
    return result;
  };

  return {
    customers,
    tiers,
    accounts,
    campaigns,
    feedback,
    summary,
    messages,
    error,
    loading,
    reload: load,

    search: useCallback(
      async term => {
        const seq = ++searchSeq.current;
        const result = await call(() => crm.listCustomers(term || null, token));
        // Only the newest request may repaint the list; a slower earlier one is
        // dropped rather than allowed to overwrite it.
        if (result.ok && seq === searchSeq.current) setCustomers(result.data);
        return result;
      },
      [token]
    ),
    customerOrders: useCallback(id => call(() => crm.customerOrders(id, token)), [token]),

    addCustomer: withReload(body => crm.createCustomer(body, token)),
    setTier: withReload((customerId, tierId) => crm.assignTier(customerId, tierId, token)),
    adjustPoints: withReload((customerId, points, reason) =>
      crm.adjustLoyalty(customerId, points, reason, token)
    ),

    openKhata: withReload(body => crm.createKhataAccount(body, token)),
    receivePayment: withReload((accountId, body) => crm.recordKhataPayment(accountId, body, token)),

    addCampaign: withReload(body => crm.createCampaign(body, token)),
    sendCampaign: withReload((id, when) => crm.scheduleCampaign(id, when, token)),
    stopCampaign: withReload(id => crm.cancelCampaign(id, token)),
    sendMessage: withReload(body => crm.queueMessage(body, token)),

    resolveFeedback: withReload((id, patch) => crm.updateFeedback(id, patch, token)),
  };
}
